import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  LuCalendar,
  LuChevronLeft,
  LuChevronRight,
  LuCircleCheck,
  LuClipboardList,
  LuClock3,
  LuEye,
  LuFilter,
  LuHourglass,
  LuPaperclip,
  LuRefreshCw,
  LuSearch,
  LuX,
} from "react-icons/lu";
import { resolvePublicFileUrl } from "../../constants/api-urls";
import {
  SUPPORT_PRIORITY_OPTIONS,
  SUPPORT_STATUS_OPTIONS,
  fetchMyAssignedDisputeById,
  fetchMyAssignedDisputeStats,
  fetchMyAssignedDisputes,
  supportDisputeExtractMessage,
} from "../../services/supportDispute.service";
import "../../assets/styles/support_disputes.scss";

const PAGE_OPTIONS = [10, 20, 50];

function formatDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  });
}

function formatTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

function formatDateTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

function formatReferenceType(type) {
  const value = String(type || "").trim();
  if (!value) return "";
  const map = {
    rfq: "RFQ",
    "request for quotation (rfq)": "RFQ",
    "request for quotation": "RFQ",
    invoice: "Invoice",
    po: "PO",
    "purchase order": "PO",
    "purchase order (po)": "PO",
    order: "Order",
    shipment: "Shipment",
    contract: "Contract",
  };
  return map[value.toLowerCase()] || value;
}

function truncateText(value, max = 28) {
  const text = String(value || "").trim();
  if (!text) return "—";
  if (text.length <= max) return text;
  return `${text.slice(0, max - 1)}…`;
}

function nameInitials(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "B";
  if (parts.length === 1) return parts[0].slice(0, 1).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

function priorityClass(priority) {
  const p = String(priority || "").toLowerCase();
  if (p === "urgent" || p === "critical") return "sd-pill sd-priority sd-priority--urgent";
  if (p === "high") return "sd-pill sd-priority sd-priority--high";
  if (p === "medium") return "sd-pill sd-priority sd-priority--medium";
  return "sd-pill sd-priority sd-priority--low";
}

function statusClass(status) {
  return `sd-pill sd-status sd-status--${String(status || "").replace(/\s+/g, "_").toLowerCase()}`;
}

function formatStatus(status, label) {
  if (label) return label;
  return String(status || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function buildPageNumbers(currentPage, totalPages, maxVisible = 5) {
  if (totalPages <= maxVisible) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }
  let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
  let end = Math.min(totalPages, start + maxVisible - 1);
  start = Math.max(1, end - maxVisible + 1);
  return Array.from({ length: end - start + 1 }, (_, index) => start + index);
}

const emptyFilters = {
  query: "",
  status: "",
  priority: "",
  buyer: "",
  seller: "",
  dateFrom: "",
  dateTo: "",
  dueToday: false,
  overdue: false,
};

export default function SupportAssignedDisputesPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [draft, setDraft] = useState({
    ...emptyFilters,
    status: searchParams.get("status") || "",
    priority: searchParams.get("priority") || "",
    dueToday: searchParams.get("dueToday") === "1",
    overdue: searchParams.get("overdue") === "1",
  });
  const [applied, setApplied] = useState(draft);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    inProgress: 0,
    resolved: 0,
    closed: 0,
  });
  const [quickView, setQuickView] = useState(null);
  const [quickViewLoading, setQuickViewLoading] = useState(false);
  const [quickViewError, setQuickViewError] = useState("");

  const loadStats = useCallback(async () => {
    try {
      const data = await fetchMyAssignedDisputeStats();
      setStats({
        total: Math.trunc(Number(data.total) || 0),
        inProgress: Math.trunc(Number(data.inProgress) || 0),
        resolved: Math.trunc(Number(data.resolved) || 0),
        closed: Math.trunc(Number(data.closed) || 0),
      });
    } catch {
      // Keep prior stats.
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchMyAssignedDisputes({
        search: applied.query.trim() || undefined,
        status: applied.status || undefined,
        priority: applied.priority || undefined,
        buyer: applied.buyer.trim() || undefined,
        seller: applied.seller.trim() || undefined,
        dateFrom: applied.dateFrom || undefined,
        dateTo: applied.dateTo || undefined,
        dueToday: applied.dueToday ? "1" : undefined,
        overdue: applied.overdue ? "1" : undefined,
        page,
        pageSize,
      });
      setItems(Array.isArray(data.items) ? data.items : []);
      setTotal(Math.trunc(Number(data.total) || 0));
    } catch (error) {
      toast.error(supportDisputeExtractMessage(error, "Failed to load assigned disputes."));
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [applied, page, pageSize]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    const timer = setTimeout(load, 200);
    return () => clearTimeout(timer);
  }, [load]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pageNumbers = useMemo(
    () => buildPageNumbers(currentPage, totalPages),
    [currentPage, totalPages],
  );

  const updateDraft = (patch) => setDraft((prev) => ({ ...prev, ...patch }));

  const applyFilters = () => {
    setApplied({ ...draft });
    setPage(1);
    const params = new URLSearchParams();
    if (draft.status) params.set("status", draft.status);
    if (draft.priority) params.set("priority", draft.priority);
    if (draft.dueToday) params.set("dueToday", "1");
    if (draft.overdue) params.set("overdue", "1");
    setSearchParams(params, { replace: true });
  };

  const resetFilters = () => {
    setDraft({ ...emptyFilters });
    setApplied({ ...emptyFilters });
    setPage(1);
    setSearchParams({}, { replace: true });
  };

  const refreshAll = async () => {
    await Promise.all([load(), loadStats()]);
  };

  const openDetail = (disputeId) => {
    navigate(`/dashboard/support/disputes/${disputeId}`);
  };

  const openQuickView = async (row) => {
    const disputeId = Number(row?.id);
    if (!Number.isFinite(disputeId) || disputeId <= 0) return;
    setQuickView({ dispute: row });
    setQuickViewError("");
    setQuickViewLoading(true);
    try {
      const detail = await fetchMyAssignedDisputeById(disputeId);
      if (detail?.dispute) {
        setQuickView(detail);
      } else {
        setQuickViewError("Dispute details are unavailable.");
      }
    } catch (error) {
      setQuickViewError(
        supportDisputeExtractMessage(error, "Failed to load dispute details."),
      );
    } finally {
      setQuickViewLoading(false);
    }
  };

  const closeQuickView = () => {
    setQuickView(null);
    setQuickViewError("");
    setQuickViewLoading(false);
  };

  const quickDispute = quickView?.dispute || null;

  return (
    <div className="sd-page sd-list-page">
      <header className="sd-head">
        <div>
          <nav className="sd-breadcrumb" aria-label="Breadcrumb">
            <Link to="/home">Home</Link>
            <span aria-hidden>&gt;</span>
            <span aria-current="page">My Assigned Disputes</span>
          </nav>
          <h1>My Assigned Disputes</h1>
          <p>Manage disputes assigned to you — update status, chat with parties, and track SLA.</p>
        </div>
        <button type="button" className="sd-btn sd-btn--ghost" onClick={refreshAll}>
          <LuRefreshCw aria-hidden /> Refresh
        </button>
      </header>

      <section className="sd-stats">
        <article className="sd-stat">
          <span className="sd-stat__icon sd-stat__icon--blue">
            <LuClipboardList aria-hidden />
          </span>
          <div className="sd-stat__body">
            <div className="sd-stat__value">{stats.total.toLocaleString()}</div>
            <div className="sd-stat__label">Total Assigned</div>
            <div className="sd-stat__hint">All time</div>
          </div>
        </article>
        <article className="sd-stat">
          <span className="sd-stat__icon sd-stat__icon--orange">
            <LuHourglass aria-hidden />
          </span>
          <div className="sd-stat__body">
            <div className="sd-stat__value">{stats.inProgress.toLocaleString()}</div>
            <div className="sd-stat__label">In Progress</div>
            <div className="sd-stat__hint">Active disputes</div>
          </div>
        </article>
        <article className="sd-stat">
          <span className="sd-stat__icon sd-stat__icon--green">
            <LuCircleCheck aria-hidden />
          </span>
          <div className="sd-stat__body">
            <div className="sd-stat__value">{stats.resolved.toLocaleString()}</div>
            <div className="sd-stat__label">Resolved</div>
            <div className="sd-stat__hint">Disputes resolved</div>
          </div>
        </article>
        <article className="sd-stat">
          <span className="sd-stat__icon sd-stat__icon--purple">
            <LuClock3 aria-hidden />
          </span>
          <div className="sd-stat__body">
            <div className="sd-stat__value">{stats.closed.toLocaleString()}</div>
            <div className="sd-stat__label">Closed</div>
            <div className="sd-stat__hint">Disputes closed</div>
          </div>
        </article>
      </section>

      <section className="sd-filters" aria-label="Filters">
        <div className="sd-filters__title">
          <LuFilter aria-hidden /> Filters
        </div>
        <div className="sd-filters__row">
          <label className="sd-search">
            <LuSearch aria-hidden />
            <input
              value={draft.query}
              onChange={(e) => updateDraft({ query: e.target.value })}
              placeholder="Search by Dispute ID, Order Ref..."
            />
          </label>
          <select
            value={draft.status}
            onChange={(e) => updateDraft({ status: e.target.value })}
          >
            <option value="">All Statuses</option>
            {SUPPORT_STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <select
            value={draft.priority}
            onChange={(e) => updateDraft({ priority: e.target.value })}
          >
            <option value="">All Priorities</option>
            {SUPPORT_PRIORITY_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <input
            type="text"
            value={draft.buyer}
            onChange={(e) => updateDraft({ buyer: e.target.value })}
            placeholder="Buyer name"
          />
          <input
            type="text"
            value={draft.seller}
            onChange={(e) => updateDraft({ seller: e.target.value })}
            placeholder="Seller name"
          />
        </div>
        <div className="sd-filters__row sd-filters__row--secondary">
          <label className="sd-date">
            <LuCalendar aria-hidden />
            <input
              type="date"
              value={draft.dateFrom}
              onChange={(e) => updateDraft({ dateFrom: e.target.value })}
              aria-label="Start date"
            />
          </label>
          <label className="sd-date">
            <LuCalendar aria-hidden />
            <input
              type="date"
              value={draft.dateTo}
              onChange={(e) => updateDraft({ dateTo: e.target.value })}
              aria-label="End date"
            />
          </label>
          <label className="sd-check">
            <input
              type="checkbox"
              checked={draft.dueToday}
              onChange={(e) =>
                updateDraft({
                  dueToday: e.target.checked,
                  overdue: e.target.checked ? false : draft.overdue,
                })
              }
            />
            Due Today
          </label>
          <label className="sd-check">
            <input
              type="checkbox"
              checked={draft.overdue}
              onChange={(e) =>
                updateDraft({
                  overdue: e.target.checked,
                  dueToday: e.target.checked ? false : draft.dueToday,
                })
              }
            />
            Overdue
          </label>
          <div className="sd-filters__actions">
            <button type="button" className="sd-btn sd-btn--ghost" onClick={resetFilters}>
              Reset
            </button>
            <button type="button" className="sd-btn sd-btn--primary" onClick={applyFilters}>
              Apply Filters
            </button>
          </div>
        </div>
      </section>

      <section className="sd-card">
        <div className="sd-table-wrap">
          <table className="sd-table sd-table--assigned">
            <thead>
              <tr>
                <th>Dispute ID</th>
                <th>Order Reference</th>
                <th>Buyer</th>
                <th>Seller</th>
                <th>Category</th>
                <th>Priority</th>
                <th>Status</th>
                <th>Submitted</th>
                <th>SLA</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={10} className="sd-empty">
                    Loading assigned disputes...
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={10} className="sd-empty">
                    No assigned disputes found.
                  </td>
                </tr>
              ) : (
                items.map((dispute) => (
                  <tr key={dispute.id} className={dispute.slaOverdue ? "is-overdue" : ""}>
                    <td>
                      <button
                        type="button"
                        className="sd-id-link"
                        onClick={() => openQuickView(dispute)}
                      >
                        {dispute.disputeNumber}
                      </button>
                    </td>
                    <td>
                      <div className="sd-stack">
                        <strong title={dispute.referenceNumber || undefined}>
                          {truncateText(dispute.referenceNumber || "—", 20)}
                        </strong>
                        <span>{formatReferenceType(dispute.referenceType) || "—"}</span>
                      </div>
                    </td>
                    <td>
                      <div className="sd-person">
                        <span className="sd-avatar">{nameInitials(dispute.buyerName)}</span>
                        <div className="sd-stack">
                          <strong title={dispute.buyerName}>
                            {truncateText(dispute.buyerName, 14)}
                          </strong>
                          <span title={dispute.buyerCompany || dispute.buyerEmail || undefined}>
                            {truncateText(dispute.buyerCompany || dispute.buyerEmail || "—", 16)}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="sd-stack">
                        <strong title={dispute.supplierName}>
                          {truncateText(dispute.supplierName, 18)}
                        </strong>
                        <span title={dispute.supplierEmail || undefined}>
                          {truncateText(dispute.supplierEmail || "—", 20)}
                        </span>
                      </div>
                    </td>
                    <td>
                      <span className="sd-cell-text" title={dispute.category}>
                        {truncateText(dispute.category, 22)}
                      </span>
                    </td>
                    <td>
                      <span className={priorityClass(dispute.priority)}>
                        {String(dispute.priority).toLowerCase() === "urgent"
                          ? "Urgent"
                          : dispute.priority}
                      </span>
                    </td>
                    <td>
                      <span className={statusClass(dispute.status)}>
                        {formatStatus(dispute.status, dispute.statusLabel)}
                      </span>
                    </td>
                    <td>
                      <div className="sd-stack">
                        <strong>{formatDate(dispute.createdAt)}</strong>
                        <span>{formatTime(dispute.createdAt)}</span>
                      </div>
                    </td>
                    <td>
                      <span
                        className={
                          dispute.slaOverdue
                            ? "sd-sla sd-sla--overdue"
                            : dispute.slaDueToday
                              ? "sd-sla sd-sla--soon"
                              : "sd-sla"
                        }
                      >
                        {dispute.slaRemainingLabel || "—"}
                      </span>
                    </td>
                    <td className="sd-actions-cell">
                      <button
                        type="button"
                        className="sd-icon-btn sd-icon-btn--ghost"
                        title="View details"
                        onClick={() => openDetail(dispute.id)}
                      >
                        <LuEye aria-hidden />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <footer className="sd-pagination">
          <div>
            Showing {items.length ? (currentPage - 1) * pageSize + 1 : 0} to{" "}
            {Math.min(currentPage * pageSize, total)} of {total} disputes
          </div>
          <div className="sd-pagination__controls">
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(1);
              }}
            >
              {PAGE_OPTIONS.map((size) => (
                <option key={size} value={size}>
                  {size} per page
                </option>
              ))}
            </select>
            <button
              type="button"
              className="sd-icon-btn"
              disabled={currentPage <= 1}
              onClick={() => setPage((v) => Math.max(1, v - 1))}
              aria-label="Previous page"
            >
              <LuChevronLeft aria-hidden />
            </button>
            {pageNumbers.map((item) => (
              <button
                key={item}
                type="button"
                className={`sd-page-btn${currentPage === item ? " is-active" : ""}`}
                onClick={() => setPage(item)}
              >
                {item}
              </button>
            ))}
            <button
              type="button"
              className="sd-icon-btn"
              disabled={currentPage >= totalPages}
              onClick={() => setPage((v) => Math.min(totalPages, v + 1))}
              aria-label="Next page"
            >
              <LuChevronRight aria-hidden />
            </button>
          </div>
        </footer>
      </section>

      {quickDispute
        ? createPortal(
            <div
              className="sd-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="sd-quick-view-title"
              onClick={closeQuickView}
            >
              <div
                className="sd-modal__panel sd-quick-view"
                onClick={(event) => event.stopPropagation()}
              >
                <header className="sd-quick-view__head">
                  <div className="sd-quick-view__head-main">
                    <div className="sd-quick-view__title-row">
                      <h2 id="sd-quick-view-title">{quickDispute.disputeNumber}</h2>
                      <span className="sd-quick-view__id-chip">
                        #{String(quickDispute.id).padStart(6, "0")}
                      </span>
                    </div>
                    <p className="sd-quick-view__subject">
                      {quickDispute.subject || "Dispute quick view"}
                    </p>
                    <div className="sd-quick-view__badges">
                      <span className={priorityClass(quickDispute.priority)}>
                        {String(quickDispute.priority).toLowerCase() === "urgent"
                          ? "Urgent"
                          : quickDispute.priority || "—"}
                      </span>
                      <span className={statusClass(quickDispute.status)}>
                        {formatStatus(quickDispute.status, quickDispute.statusLabel)}
                      </span>
                      {quickDispute.slaRemainingLabel ? (
                        <span
                          className={
                            quickDispute.slaOverdue
                              ? "sd-sla sd-sla--overdue"
                              : quickDispute.slaDueToday
                                ? "sd-sla sd-sla--soon"
                                : "sd-sla"
                          }
                        >
                          SLA: {quickDispute.slaRemainingLabel}
                        </span>
                      ) : null}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="sd-icon-btn sd-icon-btn--ghost"
                    aria-label="Close"
                    onClick={closeQuickView}
                  >
                    <LuX aria-hidden />
                  </button>
                </header>

                <div className="sd-quick-view__body">
                  {quickViewLoading ? (
                    <p className="sd-quick-view__loading">Loading dispute details...</p>
                  ) : null}
                  {quickViewError ? (
                    <p className="sd-quick-view__error">{quickViewError}</p>
                  ) : null}

                  <section className="sd-quick-view__section">
                    <h3>Dispute Details</h3>
                    <div className="sd-quick-view__fields">
                      <div className="sd-quick-view__field">
                        <span>Category</span>
                        <strong>{quickDispute.category || "—"}</strong>
                      </div>
                      <div className="sd-quick-view__field">
                        <span>Type</span>
                        <strong>{quickDispute.disputeType || "—"}</strong>
                      </div>
                      <div className="sd-quick-view__field">
                        <span>Order Reference</span>
                        <strong>
                          {quickDispute.referenceNumber || "—"}
                          {quickDispute.referenceType
                            ? ` · ${formatReferenceType(quickDispute.referenceType)}`
                            : ""}
                        </strong>
                      </div>
                      <div className="sd-quick-view__field">
                        <span>Submitted</span>
                        <strong>{formatDateTime(quickDispute.createdAt)}</strong>
                      </div>
                    </div>
                  </section>

                  <section className="sd-quick-view__section">
                    <h3>Description</h3>
                    <p className="sd-quick-view__description">
                      {quickDispute.description || "No description provided."}
                    </p>
                  </section>

                  <section className="sd-quick-view__section">
                    <h3>Attachment</h3>
                    {quickDispute.attachmentPath || quickDispute.attachmentName ? (
                      <a
                        className="sd-quick-view__attachment"
                        href={resolvePublicFileUrl(quickDispute.attachmentPath)}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <span className="sd-quick-view__attachment-icon" aria-hidden>
                          <LuPaperclip />
                        </span>
                        <span>
                          <strong>
                            {quickDispute.attachmentName || "View attachment"}
                          </strong>
                          <small>Open / download file</small>
                        </span>
                      </a>
                    ) : (
                      <p className="sd-quick-view__description sd-quick-view__description--muted">
                        No attachment submitted with this dispute.
                      </p>
                    )}
                  </section>

                  <section className="sd-quick-view__section">
                    <h3>Parties</h3>
                    <div className="sd-quick-view__parties">
                      <article className="sd-quick-view__party">
                        <span className="sd-quick-view__party-label">Buyer</span>
                        <div className="sd-person">
                          <span className="sd-avatar">
                            {nameInitials(quickDispute.buyerName)}
                          </span>
                          <div className="sd-stack">
                            <strong>{quickDispute.buyerName || "—"}</strong>
                            <span>
                              {quickDispute.buyerCompany || quickDispute.buyerEmail || "—"}
                            </span>
                            {quickDispute.buyerCompany && quickDispute.buyerEmail ? (
                              <span>{quickDispute.buyerEmail}</span>
                            ) : null}
                          </div>
                        </div>
                      </article>
                      <article className="sd-quick-view__party">
                        <span className="sd-quick-view__party-label">Seller</span>
                        <div className="sd-person">
                          <span className="sd-avatar sd-avatar--seller">
                            {nameInitials(quickDispute.supplierName)}
                          </span>
                          <div className="sd-stack">
                            <strong>{quickDispute.supplierName || "—"}</strong>
                            <span>{quickDispute.supplierEmail || "—"}</span>
                          </div>
                        </div>
                      </article>
                    </div>
                  </section>
                </div>

                <footer className="sd-quick-view__footer">
                  <button
                    type="button"
                    className="sd-btn sd-btn--ghost"
                    onClick={closeQuickView}
                  >
                    Close
                  </button>
                  <button
                    type="button"
                    className="sd-btn sd-btn--primary"
                    onClick={() => {
                      closeQuickView();
                      openDetail(quickDispute.id);
                    }}
                  >
                    Open full details
                  </button>
                </footer>
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
